// The viewer's review drafts, as writes (see pr/host.ts). Drafts live on
// the changelist server until published (GitHub's pending review would
// post every comment in it, including those the viewer did not include).
// Each is a write of its own, landed with the server's answer. None of
// them writes to GitHub but a discard of a draft that mirrors a comment of
// the viewer's pending review there, which the viewer confirms first.
import { HttpError } from '../../api/cache';
import { isNotInstalled } from '../../api/errors';
import { graphql } from '../../api/gh';
import { DELETE_REVIEW, DELETE_REVIEW_COMMENT } from '../../api/queries';
import { createDraft, deleteDraft, mirrorDrafts, restoreDraft, updateDraft, type DiscardedOnGitHub, type DraftTarget, type ReviewDraft } from '../../api/server';
import { NOT_INCLUDED, include, mirroredComment, takeOut } from '../../logic/inbox';
import { clearDraft } from '../drafts';
import { dedup, draftIdOf, isDraftCommentId, subject, withDraft, withDraftBody, withDraftState, withoutPendingReview, type PrState, type Subject } from '../optimistic';
import type { WriteSpec } from '../pending.svelte';
import { draftSubjects, type Draft, type NewHeld, type NewThread, type Reloaded, type WriteHost } from './host';

/** What a read of the viewer's pending review on GitHub left in the drafts. */
type Mirrored = { drafts: ReviewDraft[]; changed: boolean; since: number };

export class DraftWrites {
  constructor(private readonly h: WriteHost) {}

  /** Hold a new draft of the viewer's (not included, like every draft), shown at once. */
  addDraft(
    target: DraftTarget,
    body: string,
    w: Pick<WriteSpec<PrState, Reloaded, Subject, ReviewDraft>, 'kind' | 'label' | 'done' | 'dedup' | 'draft'>,
  ) {
    // Its place is on the pull request: none before it has loaded.
    if (!this.h.serverData || this.h.head() === null) return Promise.resolve(false);
    const at = new Date().toISOString();
    const shown: ReviewDraft = { id: this.h.tempId(), target, body, state: NOT_INCLUDED, author: 'user', origin: { kind: 'app' }, created_at: at, updated_at: at };
    const since = this.h.drops;
    const era = this.discards;
    this.draftEra.set(shown.id, era);
    const created = this.h.writes.run<ReviewDraft>({
      ...w,
      subject: draftSubjects(shown.id),
      apply: withDraft(shown),
      send: () => createDraft(this.h.owner, this.h.repo, this.h.number, { target, body }),
      confirm: {
        land: (created) => {
          this.draftEra.set(created.id, era);
          this.h.landDraft(created, since);
        },
      },
    });
    this.creating.add(created);
    void created.finally(() => this.creating.delete(created));
    return created;
  }

  /** A draft comment on lines of the diff (a new review thread when published). Shown at once, on those lines. */
  addThread(t: NewThread, draft: Draft) {
    const commit = this.h.commentCommit();
    const target: DraftTarget =
      t.subjectType === 'FILE' || t.line === null
        ? { kind: 'file', path: t.path, commit_sha: commit }
        : {
            kind: 'line',
            path: t.path,
            side: t.side,
            line: t.line,
            start_line: t.startLine && t.startLine < t.line ? t.startLine : null,
            commit_sha: commit,
          };
    return this.addDraft(target, t.body, { kind: 'thread', label: 'Saving draft', done: 'Draft saved', dedup: dedup.draft(draft.key), draft });
  }

  /**
   * A file-level draft (a comment on lines outside the diff hunks of a file
   * the PR changes): a file-level review thread when published.
   */
  addFileThread(path: string, body: string, draft: Draft) {
    return this.addDraft({ kind: 'file', path, commit_sha: this.h.commentCommit() }, body, {
      kind: 'thread',
      label: 'Saving draft',
      done: 'Draft saved',
      dedup: dedup.draft(draft.key),
      draft,
    });
  }

  /** A draft on a file the PR does not change: posted to the conversation when published. */
  addHeld(c: NewHeld, draft: Draft) {
    return this.addDraft({ kind: 'conversation', path: c.path, side: c.side, line: c.line, commit_sha: c.commit_sha }, c.body, {
      kind: 'held-add',
      label: 'Saving draft',
      done: 'Held comment saved',
      dedup: dedup.draft(draft.key),
      draft,
    });
  }

  /** Edit a draft's text; its state stays. Shown at once. */
  editDraft(id: ReviewDraft['id'], body: string, draft: Draft) {
    if (!this.h.serverData) return Promise.resolve(false);
    const since = this.h.drops;
    return this.h.writes.run({
      kind: 'held-edit',
      label: 'Saving draft',
      done: 'Draft saved',
      dedup: dedup.draftItem(id),
      subject: draftSubjects(id),
      draft,
      apply: withDraftBody(id, body, new Date().toISOString()),
      send: () => updateDraft(this.h.owner, this.h.repo, this.h.number, id, { body }),
      confirm: { land: (next) => this.h.landDraft(next, since) },
    });
  }

  /**
   * Include a draft in the review being prepared (tick it: the next publish
   * sends it), or take it out (it stays a draft). Only the latest choice is
   * sent.
   */
  includeDraft(id: ReviewDraft['id'], on: boolean) {
    const at = new Date().toISOString();
    // Included on the head this page shows (a stale one only makes the publish's check stricter);
    // none before the pull request has loaded.
    const head = this.h.head();
    if (!this.h.serverData || (on && head === null)) return Promise.resolve(false);
    const since = this.h.drops;
    return this.h.writes.run({
      kind: 'draft-state',
      label: on ? 'Including draft' : 'Taking draft out',
      done: on ? 'Included in this review' : 'Not included',
      dedup: dedup.draftState(id),
      latest: true,
      subject: draftSubjects(id),
      apply: withDraftState(id, on && head !== null ? include(at, head) : takeOut(), at),
      send: () => updateDraft(this.h.owner, this.h.repo, this.h.number, id, on && head !== null ? { state: 'accepted', head } : { state: 'toReview' }),
      confirm: { land: (next) => this.h.landDraft(next, since) },
    });
  }

  /**
   * Discard a draft; it stays on screen, marked as being discarded, until
   * the server confirms. `editKey`: its edit box's saved text, which goes
   * with it. `quiet`: the caller tells the user itself (e.g. a toast with
   * Undo), so the write announces nothing. A draft that mirrors a comment
   * of the viewer's pending review on GitHub has that comment deleted
   * there by the server while GitHub shows it still pending (ask first:
   * `mirroredComment`); `onGitHub` is told what the server found, and the
   * pull request is read again.
   */
  discardDraft(id: ReviewDraft['id'], editKey?: string, { quiet = false, onGitHub }: { quiet?: boolean; onGitHub?: (found: DiscardedOnGitHub) => void } = {}) {
    if (!this.h.serverData) return Promise.resolve(false);
    return this.h.writes.run({
      kind: 'held-delete',
      label: 'Discarding draft',
      done: () => (quiet ? null : 'Draft discarded'),
      dedup: dedup.draftItem(id),
      subject: draftSubjects(id),
      send: () => deleteDraft(this.h.owner, this.h.repo, this.h.number, id),
      confirm: {
        land: (found) => {
          if (editKey) clearDraft(editKey);
          this.h.dropDrafts([id]);
          onGitHub?.(found);
          // It mirrored a comment on GitHub: the threads are read again, so they show what GitHub has now.
          if (found) void this.h.load();
        },
      },
    });
  }

  /** Bring back a draft discarded a moment ago (Undo), as it was: the same place, text, author and state. */
  restoreDraft(d: ReviewDraft) {
    if (!this.h.serverData) return Promise.resolve(false);
    const since = this.h.drops;
    return this.h.writes.run({
      kind: 'held-add',
      label: 'Restoring draft',
      done: 'Draft restored',
      dedup: dedup.draftItem(d.id),
      subject: draftSubjects(d.id),
      apply: withDraft(d),
      send: () => restoreDraft(this.h.owner, this.h.repo, this.h.number, d.id),
      confirm: { land: (back) => this.h.landDraft(back, since) },
    });
  }

  /**
   * Read the viewer's pending review on GitHub (one made on github.com, or
   * by a tool through GitHub's API) and mirror it in the drafts, not
   * included, so the viewer decides here what is sent. Nothing is written
   * to GitHub: the pending review stays as it is, and its mirrored comments
   * are shown once, as their drafts. Reading again follows what changed on
   * GitHub since. While a publish runs it is left alone (nothing to do now).
   */
  mirrorDrafts(): Promise<boolean> {
    // Nothing to read: no pending review, and no draft that mirrored one.
    if (!this.h.pendingReviewId && !this.h.server.drafts.some((d) => mirroredComment(d) !== null)) return Promise.resolve(true);
    if (!this.h.serverData) return Promise.resolve(false);
    return this.h.writes.run({
      kind: 'mirror',
      label: 'Reading your pending review on GitHub',
      // Said only when the drafts changed.
      done: (read: Mirrored | null) => (read?.changed ? 'Your drafts now show your pending review on GitHub' : null),
      dedup: dedup.mirrorDrafts,
      send: async (): Promise<Mirrored | null> => {
        // The list it answers with is as of some moment while it ran: a draft write answered meanwhile is newer.
        const since = this.h.draftWrites;
        try {
          return { ...(await mirrorDrafts(this.h.owner, this.h.repo, this.h.number)), since };
        } catch (e) {
          // A publish of these drafts is running, and the pending review is its own: nothing to read now.
          if (e instanceof HttpError && (e.body as { error?: string } | null)?.error === 'publish_running') return null;
          // The server keeps no drafts here (see `serverData`): said once by the banner, not as a failure.
          if (isNotInstalled(e)) {
            this.h.notInstalled();
            return null;
          }
          throw e;
        }
      },
      confirm: { land: (read) => read && this.h.send({ t: 'drafts-mirrored', drafts: read.drafts, since: read.since }) },
    });
  }

  /** A draft reply on review thread `threadId`, shown at once at its end. */
  reply(threadId: string, body: string, draft: Draft) {
    return this.addReply(threadId, body, { kind: 'reply', label: 'Saving reply', done: 'Reply saved as a draft', dedup: dedup.draft(draft.key), draft });
  }

  addReply(threadId: string, body: string, w: Pick<WriteSpec<PrState, Reloaded, Subject, ReviewDraft>, 'kind' | 'label' | 'done' | 'dedup' | 'draft'>) {
    const t = this.h.server.threads.find((x) => x.id === threadId);
    const target: DraftTarget = { kind: 'reply', thread_id: threadId, path: t?.path ?? '', line: t ? (t.line ?? t.originalLine) : null };
    return this.addDraft(target, body, w);
  }

  /**
   * Done / Ack: add the word as a draft reply. The thread is resolved when
   * the review is published (see `resolveOnPublish`), so nobody sees it
   * resolved before they can see why.
   */
  quickReply(threadId: string, word: 'Done' | 'Ack') {
    // Done or Ack, once per thread: a manual Done retires a failed one's Retry.
    return this.addReply(threadId, word, { kind: 'quick-reply', label: `Saving “${word}”`, done: `“${word}” saved as a draft`, dedup: dedup.quick(threadId) });
  }

  /**
   * Delete a draft shown in a review thread: one of changelist's drafts is
   * discarded; a comment of a pending review made on github.com is deleted
   * on GitHub. It stays on screen, marked as being deleted, until confirmed.
   */
  deleteDraftComment(commentId: string) {
    const own = draftIdOf(commentId, this.h.server.drafts);
    if (own !== null) return this.discardDraft(own);
    // One of changelist's drafts not known here (e.g. still being created): never GitHub's to delete.
    if (isDraftCommentId(commentId)) return Promise.resolve(false);
    return this.h.write({
      kind: 'delete-draft',
      dedup: dedup.comment(commentId),
      label: 'Deleting draft',
      done: 'Draft deleted',
      subject: subject.comment(commentId),
      send: async () => {
        // Already deleted (e.g. on GitHub) is what was asked for.
        await graphql(DELETE_REVIEW_COMMENT, { input: { id: commentId } }, { notFoundOk: true });
      },
    });
  }

  /** Discards of every draft started so far; a draft created in this tab records the count it was created at (its era). */
  private discards = 0;

  private draftEra = new Map<ReviewDraft['id'], number>();

  /** Creates being sent (see `addDraft`). */
  private creating = new Set<Promise<boolean>>();

  /**
   * Discard every draft (Discard drafts), and the viewer's pending review
   * on GitHub if there is one (the viewer confirms that first): they leave
   * the screen at once. Every draft
   * there is when it starts goes, those whose create is still being sent
   * included (it waits for them); one written after it started, or one
   * that arrives from elsewhere (an agent's) while it runs, stays.
   */
  discardAllDrafts(): Promise<boolean> {
    const era = this.discards++;
    // The drafts the server had when it started, and those this tab began creating before; never one that arrives later.
    const atStart = new Set(this.h.server.drafts.map((d) => d.id));
    const covered = (d: ReviewDraft) => atStart.has(d.id) || (this.draftEra.get(d.id) ?? Infinity) <= era;
    const creates = [...this.creating];
    const review = this.h.pendingReviewId;
    if (!this.h.server.drafts.length && !creates.length && !review) return Promise.resolve(true);
    if (!this.h.serverData) return Promise.resolve(false);
    return this.h.writes.run({
      kind: 'discard',
      dedup: dedup.discardDrafts,
      label: 'Discarding drafts',
      done: 'Drafts discarded',
      apply: (s) => withoutPendingReview({ ...s, drafts: s.drafts.filter((d) => !covered(d)) }),
      send: async () => {
        await Promise.all(creates);
        for (const id of this.h.server.drafts.filter(covered).map((d) => d.id)) {
          await deleteDraft(this.h.owner, this.h.repo, this.h.number, id);
          this.h.dropDrafts([id]);
        }
        if (review) await graphql(DELETE_REVIEW, { input: { pullRequestReviewId: review } }, { notFoundOk: true });
      },
      confirm: this.h.byPrLoad,
    });
  }
}
